import { useCallback, useMemo } from "react"

import { useMatch, useParams } from "react-router-dom"

import { useImageCollectionDispatchContext } from "@l2r-front/l2r-images"
import {
    Layer,
    REFERENTIAL_LAYER,
    CATALOG_ITEMS_LAYER,
    CATALOG_ITEMS_SOURCE,
    CATALOG_ITEMS_LAYER_CLUSTER,
    CATALOG_ITEMS_LAYER_CLUSTER_COUNT,
    getClusterLayerConfig,
    getClusterCountLayerConfig,
    useOrderingLayers,
    useMapDispatchContext,
} from "@l2r-front/l2r-map"
import { useNetworksDispatchContext } from "@l2r-front/l2r-networks"
import { useTheme } from "@l2r-front/l2r-ui"
import { useNavigateWithSearchParams } from "@l2r-front/l2r-utils"

import { getCatalogItemLayerStyle } from "../../utils/getCatalogItemLayerStyle"

const CLUSTER_SIZE = 30
const MAX_ZOOM_LEVEL_CLUSTERED = 16

export const ICON_SIZE = 60
export const COLOR_SIZE = 20

const EMPTY_PARAMS: string[] = []

export function CatalogItemLayers(props: CatalogItemLayersProps) {

    const {
        paramsToDeleteOnClick = EMPTY_PARAMS,
        Source,
        sourceProps,
    } = props

    const theme = useTheme()
    const params = useParams()
    const { setSelectedLinearLocations } = useNetworksDispatchContext()
    const { setImageFeatureClickedPosition, setSegmentFeatureIdClicked } = useImageCollectionDispatchContext()
    const road = params?.["road"] !== "code" ? params?.["road"] : null
    const signId = params?.["road"] !== "code" ? params?.["signId"] : null
    const { setMapPin, storeMapBoundingBox } = useMapDispatchContext()
    const matchCode = useMatch(":slugId/:app/:module/code/:code/*")
    const matchRoadCode = useMatch(":slugId/:app/:module/:road/code/:code/*")

    useOrderingLayers(["road-number", "road-number-shield", CATALOG_ITEMS_LAYER, CATALOG_ITEMS_LAYER_CLUSTER, CATALOG_ITEMS_LAYER_CLUSTER_COUNT])

    const navigateWithSearchParams = useNavigateWithSearchParams()
    const catalogItemsLayerConfig = useMemo(() => {
        if (!theme) {
            return null
        }

        return getCatalogItemLayerStyle(signId, road)
    }, [signId, road, theme])

    const catalogItemsClusterLayerConfig = useMemo(() => {
        if (!theme) {
            return null
        }
        return getClusterLayerConfig({
            paint: {
                "circle-color": theme.palette["common/gray_states"].dark,
                "circle-radius": CLUSTER_SIZE,
            },
        })
    }, [theme])

    const catalogItemsClusterCountLayerConfig = useMemo(() => {
        if (!theme) {
            return null
        }
        return getClusterCountLayerConfig({
            layout: {
                "text-size": theme.typography["Desktop & Mobile/Heading/H2"].fontSize,
            },
            paint: {
                "text-color": theme.palette.map.text,
            },
        })
    }, [theme])

    const handleClusterLayerClick = useCallback((event: any) => {
        const map = event.target

        const feature = event.feature
        const geometry = feature.geometry
        const clusterId = feature?.properties?.["cluster_id"]

        const source = map.getSource(CATALOG_ITEMS_SOURCE)
        source.getClusterExpansionZoom(clusterId, (error: any, zoom: any) => {
            if (error) {
                return
            }
            map.easeTo({
                center: geometry.coordinates,
                zoom: zoom + 1,
            })
        })
    }, [])

    const handleSignLayerClick = useCallback((event: any) => {
        const map = event.target
        const roadFeatures = map.queryRenderedFeatures({
            layers: [REFERENTIAL_LAYER],
        })
        const feature = event.feature
        const roadFeatureClicked = roadFeatures.find((roadFeature: any) => {
            const roadFeatureRoad = JSON.parse(roadFeature.properties.linearLocation)[0]["road"]
            const roadFeatureEvent = JSON.parse(feature.properties.linearLocation).road
            return roadFeatureRoad === roadFeatureEvent
        })
        setMapPin()
        setSelectedLinearLocations(({
            ...feature,
            properties: {
                ...feature.properties,
                linearLocation: JSON.stringify([JSON.parse(feature.properties.linearLocation)]),
            },
        }))
        const linearLocation = JSON.parse(feature.properties.linearLocation)
        setSegmentFeatureIdClicked(roadFeatureClicked?.properties?.uuid)
        const newLocation = (matchRoadCode || matchCode) ? `../${linearLocation.road}/code/${(matchRoadCode || matchCode)?.params?.code}/${feature?.properties?.uuid}`
            : (signId || road) ? `../${linearLocation.road}/${feature?.properties?.uuid}`
                : `${linearLocation.road}/${feature?.properties?.uuid}`
        storeMapBoundingBox()
        setImageFeatureClickedPosition(feature.geometry.coordinates)
        return navigateWithSearchParams(newLocation, { paramsToDelete: paramsToDeleteOnClick })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matchCode,
        matchRoadCode,
        navigateWithSearchParams,
        signId,
        road,
        paramsToDeleteOnClick,
        setImageFeatureClickedPosition,
        setSegmentFeatureIdClicked,
        setSelectedLinearLocations,
        storeMapBoundingBox])

    const SourceComponent = Source

    return <SourceComponent
        id={CATALOG_ITEMS_SOURCE}
        cluster={true}
        clusterMaxZoom={MAX_ZOOM_LEVEL_CLUSTERED}
        {...sourceProps}>
        <Layer
            {...catalogItemsLayerConfig}
            id={CATALOG_ITEMS_LAYER}
            onClick={handleSignLayerClick}
        />
        <Layer
            {...catalogItemsClusterLayerConfig}
            id={CATALOG_ITEMS_LAYER_CLUSTER}
            onClick={handleClusterLayerClick}
        />
        <Layer
            {...catalogItemsClusterCountLayerConfig}
            id={CATALOG_ITEMS_LAYER_CLUSTER_COUNT}
        />
    </SourceComponent>
}
type CatalogItemLayersProps = {
    Source: React.ElementType
    paramsToDeleteOnClick?: Array<string>
    sourceProps?: Record<string, any>
}
